<template>
  <div class="p-grid">
    <Toast />

    <div class="p-col-12">
      <Breadcrumb :home="breadcrumbHome" :model="breadcrumbItems" />
    </div>

    <div class="p-col-12">
      <div class="card" v-if="loadingIndicator">
        <p>Loading, please wait . . .</p>
        <ProgressBar mode="indeterminate" style="height: .5em" />
      </div>

      <div class="card">
        <div class="p-d-flex p-jc-between">
          <div><h3>Location Configuration</h3></div>
        </div>

        <Message v-for="(msg, index) in messages" :severity="msg.severity" :key="index" :life="5000" :sticky="false">{{msg.content}}</Message>

        <Toolbar class="p-mb-4">
          <template #start>
            <Button v-if="permited(['location.*', 'location.add'])" label="Add Location" icon="pi pi-plus" class="p-button-success" style="margin-right: .5em" @click="addLocation()"/>
          </template>

          <template #end>
            <Button label="Search" icon="pi pi-search" class="p-button-primary p-mr-2" @click="toggleSearch()"/>
            <Button icon="pi pi-refresh" class="p-button-help" @click="getLocationList()" />
          </template>
        </Toolbar>

        <div class="card" v-if="displaySearch">
          <h5>Location Search</h5>
          <div class="p-field p-grid">
            <label class="p-col-12 p-mb-2 p-sm-12 p-md-2 p-mb-md-0">Rack/Row/Slot Number</label>
            <div class="p-col-12 p-sm-12 p-md-10">
              <span class="p-input-icon-left">
                <i class="pi pi-search" />
                <InputText v-model="filters['global'].value" placeholder="Search" />
              </span>
            </div>
          </div>
          <div class="p-field p-grid">
            <label class="p-col-12 p-mb-2 p-sm-12 p-md-2 p-mb-md-0">Media Type</label>
            <div class="p-col-12 p-sm-12 p-md-10">
              <Dropdown v-model="filters['type'].value" :options="options.mtype" optionLabel="name" optionValue="code" placeholder="Select Media Type" :filter="true" filterPlaceholder="Find Media Type" :showClear="true"/>
            </div>
          </div>
          <div class="p-field p-grid">
            <label class="p-col-1 p-mb-2 p-sm-12 p-md-2 p-mb-md-0">Customer Name</label>
            <div class="p-col-12 p-sm-12 p-md-10">
              <Dropdown v-if="permited(['location.*', 'location.view'])" v-model="filters['customer.name'].value" :options="options.customers" optionLabel="name" optionValue="name" placeholder="Select Customer" :filter="true" filterPlaceholder="Find Customer" :showClear="true"/>
            </div>
          </div>
        </div>

        <DataTable :value="locations" :expandedRows.sync="expandedRows" filterMode="lenient" :paginator="true" class="p-datatable-customers" :rows="10" dataKey="id" :rowHover="true" :filters.sync="filters" filterDisplay="menu" :loading="tableLoading" paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown" :rowsPerPageOptions="[10,25,50,100]" :globalFilterFields="['customer.name','customer.code','rack_label','row_label']" currentPageReportTemplate="Showing {first} to {last} of {totalRecords} locations" >
          <template #header>
            <div class="table-header">
              List of Locations
            </div>
          </template>

          <template #empty>
            No locations found.
          </template>

          <template #loading>
            Loading locations data. Please wait.
          </template>

          <Column :expander="true" />
          <Column field="rack_label" header="Rack" sortable filterField="rack_label" :bodyStyle="{'text-transform':'uppercase'}"/>
          <Column field="row_label" header="Row" sortable filterField="row_label" :bodyStyle="{'text-transform':'uppercase'}"/>

          <Column header="Media Type" field="type" sortable :bodyStyle="{'text-transform':'uppercase'}">
            <template #body="slotProps">
              {{ mapType(slotProps.data.type) }}
            </template>
          </Column>

          <Column field="customer.name" header="Customer" sortable filterField="customer.name" :bodyStyle="{'text-transform':'uppercase'}"/>
          <template #expansion="slotProps">
            <div class="orders-subtable">
              <h5>Slots at [{{slotProps.data.rack_label}}] {{slotProps.data.row_label}}</h5>
              <DataTable :value="slotProps.data.slots">
                <Column field="slot_label" header="Slot Label" sortable :bodyStyle="{'text-transform':'uppercase'}"/>
                <Column field="is_occupied" header="Slot Status" sortable :bodyStyle="{'text-transform':'uppercase'}">
                  <template #body="slotProps">{{ slotProps.data.is_occupied ? 'Occupied' : 'Available' }}</template>
                </Column>
              </DataTable>
            </div>
          </template>

          <Column v-if="permited(['location.*', 'location.update'])" bodyStyle="text-align: center; overflow: visible;" header="Action" headerStyle="text-align: center;">
            <template #body="slotProps">
              <Button icon="pi pi-pencil" class="p-button-rounded p-button-warning p-mr-2" @click="editLocation(slotProps.data)"/>
              <!-- <Button icon="pi pi-trash" class="p-button-rounded p-button-danger p-mr-2" @click="confirmDeleteLocation(loc.data)"/> -->
            </template>
          </Column>
        </DataTable>

        <Dialog :visible.sync="addLocationDialog" class="p-fluid" :header="(editLocationDialog ? 'Update' : 'Add') + ' Row'" :style="{width: '70vw'}" :modal="true">
          <form class="form-horizontal form-label-left" @submit.prevent="addLocation">
            <div class="p-formgrid p-grid">
              <div class="p-field p-col-12 p-md-6">
                <label for="rack_label">Rack Label <span class="required" style="color:red;">*</span></label>
                <InputText id="rack_label" class="p-text-uppercase" name="rack_label" v-model="input.rack_label.value" placeholder="Rack Label" :class="input.rack_label.errMsg ? 'p-invalid' : null" :required="true"/>
                <small class="p-invalid" v-if="input.rack_label.errMsg">{{ input.rack_label.errMsg }}</small>
              </div>

              <div class="p-field p-col-12 p-md-6">
                <label for="row_label">Row Label <span class="required" style="color:red;">*</span></label>
                <InputText id="row_label" class="p-text-uppercase" name="row_label" v-model="input.row_label.value" placeholder="Row Label" :class="input.row_label.errMsg ? 'p-invalid' : null" :required="true"/>
                <small class="p-invalid" v-if="input.row_label.errMsg">{{ input.row_label.errMsg }}</small>
              </div>

              <div class="p-field p-col-12 p-md-6">
                <label for="type">Media Type <span class="required" style="color:red;">*</span></label>
                <Dropdown id="type" class="p-text-uppercase" name="type" v-model="input.type.value" :options="options.mtype" optionLabel="name" placeholder="Please Select" appendTo="body" :class="input.type.errMsg ? 'p-invalid' : null" :showClear="true" :required="true"/>
                <small class="p-invalid" v-if="input.type.errMsg">{{ input.type.errMsg }}</small>
              </div>

              <div class="p-field p-col-12 p-md-6">
                <label for="total_slots">Total Slot <span class="required" style="color:red;">*</span></label>
                <InputText id="total_slots" class="p-text-uppercase" name="total_slots" v-model="input.total_slots.value" placeholder="Rack Label" :class="input.total_slots.errMsg ? 'p-invalid' : null" :required="true"/>
                <small class="p-invalid" v-if="input.total_slots.errMsg">{{ input.total_slots.errMsg }}</small>
              </div>

              <div class="p-field p-col-12 p-md-6">
                <label for="company">Customer Name <span v-if="editLocationDialog" class="required" style="color:red;">*</span></label>
                <Dropdown id="company" class="p-text-uppercase" name="company" v-model="input.company.value" :options="options.customers" optionLabel="name" placeholder="Please Select" appendTo="body" :class="input.company.errMsg ? 'p-invalid' : null" :showClear="true" :required="true"/>
                <small class="p-invalid" v-if="input.company.errMsg">{{ input.company.errMsg }}</small>
              </div>
            </div>
          </form>

          <template #footer>
            <Button v-if="!editLocationDialog" icon="pi pi-check" label="Submit" class="p-button-primary" @click="submitNewLocation()"/>
            <Button v-if="editLocationDialog" icon="pi pi-check" label="Update" class="p-button-primary" @click="submitLocationUpdate()"/>
          </template>
        </Dialog>
      </div>
    </div>
  </div>
</template>

<script>
import { getLocations, storeLocation, updateLocation} from '@/api/location'

export default {
  name: "LocationConfiguration",
  data() {
    return {
      breadcrumbHome: { icon: 'pi pi-home', to: { name: 'dashboard' } },
      breadcrumbItems: [
        { label:'Configuration'},
        { label:'Location', to: { name: 'location.index' } }
      ],
      messages: [],
      loadingIndicator: false,
      expandedRows: [],

      locations: [],
      tableLoading: true,
      filters: {
        'global': { value: null },
        'type': { value: null },
        'customer.name': {value: null},
      },

      locationData: {
        slots: [],
        customer: []
      },
      locationDialog: false,
      deleteLocationDialog: false,
      displayConfirmation: false,

      displaySearch: false,

      addLocationDialog: false,
      editLocationDialog: false,
      input: {
        rack_label: { value: '', errMsg: null },
        row_label: { value: '', errMsg: null },
        type: { value: [], errMsg: null },
        total_slots: { value: '', errMsg: null },
        company: { value: [], errMsg: null },
      },

      options: {
        mtype: [],
        customers: []
      },
    }
  },

  created() {
    let flashMessage = this.$session.flash.get("flash-message")
    if(flashMessage) {
      this.messages.push({ severity: this.$session.flash.get("flash-class"), content: flashMessage })
    }

    this.$store.getters.options.companies.forEach(element => {
      this.options.customers.push({ code: element.code, name: element.display })
    })

    this.$store.getters.options.mtype.forEach(element => {
      this.options.mtype.push({ code: element.code, name: element.display })
    })

    this.getLocationList()
  },

  methods: {
    getLocationList() {
      this.tableLoading = true

      try {
        getLocations()
          .then((res) => {
            // if (res.data.total == 0) {
            //   this.isLoadNull = true
            // }

            if(res.data.status) {
              this.locations = res.data.data
            } else {
              this.$toast.add({ severity: 'error', content: res.data.data.message })
            }
          })
        .catch(() => {
          this.$toast.add({ severity: 'error', content: "Failed to get location list" })
        })
        .finally(() => { this.tableLoading = false })
      } catch (e) {
        console.error(e)
        this.$toast.add({severity:'error', summary: 'Error', detail: 'Failed to get location list', life: 5000})
      }
    },

    addLocation() {
      try {
        this.clearLocationData()
        this.addLocationDialog = true
        this.editLocationDialog = false
      } catch (e) {
        this.$toast.add({severity:'error', summary: 'Error', detail: "Failed to display add location form", life: 5000})
      }
    },

    submitNewLocation() {
      // Form Validation
      let doSubmit = true
      doSubmit = this.inputValidation(doSubmit)

      // Submit
      if (doSubmit) {
        this.loadingIndicator = true

        let newLocationObj = {
          rack_label: this.input.rack_label.value,
          row_label: this.input.row_label.value,
          type: this.input.type.value.code,
          total_slots: this.input.total_slots.value,
          customer_id: this.input.company.value ? this.input.company.value.code : null
        }

        try {
          storeLocation(newLocationObj)
            .then((res) => {
              if (res.data.status) {
                this.messages.push({ severity: 'success', content: res.data.message })

                this.getLocationList()
                this.addLocationDialog = false
              } else {
                this.$toast.add({severity:'error', summary: 'Error', detail: res.data.message, life: 5000})
              }
            })
            .catch((e) => {
              if(e.response.status && e.response.status == 422) {
                this.$toast.add({severity:'error', summary: 'Error', detail: e.response.data.message, life: 5000})

                if(e.response.data.errors) {
                  let errObj = e.response.data.errors
                  this.input.rack_label.errMsg = errObj.rack_label ? errObj.rack_label[0] : null
                  this.input.row_label.errMsg = errObj.row_label ? errObj.row_label[0] : null
                  this.input.type.errMsg = errObj.type ? errObj.type[0] : null
                  this.input.total_slots.errMsg = errObj.total_slots ? errObj.total_slots[0] : null
                  this.input.company.errMsg = errObj.customer_id ? errObj.customer_id[0] : null
                }
              } else {
                this.$toast.add({severity:'error', summary: 'Error', detail: "Location registration failed due to network error.", life: 5000})
              }
            })
            .finally(() => this.loadingIndicator = false)
        } catch (e) {
          console.error(e)
          this.loadingIndicator = false
        }
      }

      window.scrollTo(0,0)
    },

    mapType(type) {
      let typeName = this.options.mtype.find(element => element.code === type)
      return typeName ? typeName.name : null
    },

    editLocation(data) {
      this.loadingIndicator = true
      this.locationDialog = false
      this.clearLocationData()

      this.locationData = data

      this.input.rack_label.value = data.rack_label
      this.input.row_label.value = data.row_label
      this.input.total_slots.value = data.total_slots

      // Populate media type selections
      this.options.mtype.forEach(element => { (element.code == data.type) ? this.input.type.value = element : null })

      // Populate company selections
      this.options.customers.forEach(element => { (element.code == data.customer_id) ? this.input.company.value = element : null })

      this.addLocationDialog = true
      this.editLocationDialog = true
      this.loadingIndicator = false
    },

    submitLocationUpdate() {
      // Form Validation
      let doSubmit = true
      doSubmit = this.inputValidation(doSubmit)

      // Submit
      if (doSubmit) {
        this.loadingIndicator = true

        let updateLocationObj = {
          rack_label: this.input.rack_label.value,
          row_label: this.input.row_label.value,
          type: this.input.type.value.code,
          total_slots: this.input.total_slots.value,
          customer_id: this.input.company.value ? this.input.company.value.code : null
        }

        try {
          updateLocation(this.locationData.id, updateLocationObj)
            .then((res) => {
              if (res.data.status) {
                this.messages.push({ severity: 'success', content: res.data.message })

                this.getLocationList()
                this.addLocationDialog = false
              } else {
                this.$toast.add({severity:'error', summary: 'Error', detail: res.data.message, life: 5000})
              }
            })
            .catch((e) => {
              if(e.response.status && e.response.status == 422) {
                this.$toast.add({severity:'error', summary: 'Error', detail: e.response.data.message, life: 5000})

                if(e.response.data.errors) {
                  let errObj = e.response.data.errors
                  this.input.rack_label.errMsg = errObj.rack_label ? errObj.rack_label[0] : null
                  this.input.row_label.errMsg = errObj.row_label ? errObj.row_label[0] : null
                  this.input.type.errMsg = errObj.type ? errObj.type[0] : null
                  this.input.total_slots.errMsg = errObj.total_slots ? errObj.total_slots[0] : null
                  this.input.company.errMsg = errObj.customer_id ? errObj.customer_id[0] : null
                }
              } else {
                this.$toast.add({severity:'error', summary: 'Error', detail: "Location failed to update due to network error.", life: 5000})
              }
            })
            .finally(() => this.loadingIndicator = false)
        } catch (e) {
          console.error(e)
          this.loadingIndicator = false
        }
      }
    },

    clearLocationData() {
      this.locationData = {
        slots: [],
        customer: []
      }

      this.input = {
        rack_label: { value: '', errMsg: null },
        row_label: { value: '', errMsg: null },
        type: { value: [], errMsg: null },
        total_slots: { value: '', errMsg: null },
        company: { value: [], errMsg: null }
      }
    },

    inputValidation(doSubmit) {
      // Form Validation
      this.input.rack_label.errMsg = null
      this.input.row_label.errMsg = null
      this.input.type.errMsg = null
      this.input.total_slots.errMsg = null
      this.input.company.errMsg = null

      if (!this.input.rack_label.value) {
        doSubmit = false
        this.input.rack_label.errMsg = "Rack Label is required"
      }

      if (Number.isInteger(Number(this.input.row_label.value))) {
        if(Number(this.input.row_label.value) < 1) {
          doSubmit = false
          this.input.row_label.errMsg = "Row Label is required"
        }
      } else {
        doSubmit = false
        this.input.row_label.errMsg = "Row Label must be an integer"
      }

      if (!this.input.type.value.code) {
        doSubmit = false
        this.input.type.errMsg = "Media Type is required"
      }

      if (Number.isInteger(Number(this.input.total_slots.value))) {
        if(Number(this.input.total_slots.value) < 1) {
          doSubmit = false
          this.input.total_slots.errMsg = "Total Slot is required"
        }
      } else {
        doSubmit = false
        this.input.total_slots.errMsg = "Total Slot must be an integer"
      }

      return doSubmit
    },

    toggleSearch() {
      this.displaySearch = !this.displaySearch
    },

    permited(permissions) {
      return this.$parent.permited(permissions)
    }
  }
}
</script>

<style scoped lang="scss">
.p-dropdown {
    width: 14.4rem;
    margin-right: 5px;
}
.p-datatable .p-column-filter {
  display: none;
}
.orders-subtable {
  padding: 1rem;
}

/deep/ .p-fluid.p-info.p-dialog.p-component {
  .p-dialog-title {
    text-transform: uppercase;
  }
}

.table-header {
  display: flex;
  justify-content: space-between;
}

i, span {
  vertical-align: middle;
}

span {
  margin: 0 .5rem;
}

/deep/ .p-datatable.p-datatable-customers {
  .p-datatable-header {
    padding: 1rem;
    text-align: left;
    font-size: 1.5rem;
  }

  .p-paginator {
    padding: 1rem;
  }

  .p-datatable-thead > tr > th {
    text-align: center;
  }

  .p-datatable-tbody > tr > td {
    cursor: auto;
  }

  .p-dropdown-label:not(.p-placeholder) {
    text-transform: uppercase;
  }
}

/* Responsive */
.p-datatable-customers .p-datatable-tbody > tr > td .p-column-title {
  display: none;
}

@media screen and (max-width: 960px) {
  /deep/ .p-datatable {
    &.p-datatable-customers {
      .p-datatable-thead > tr > th,
      .p-datatable-tfoot > tr > td {
        display: none !important;
      }

      .p-datatable-tbody > tr {
        border-bottom: 1px solid var(--layer-2);

        > td {
          text-align: left;
          display: block;
          border: 0 none !important;
          width: 100% !important;
          float: left;
          clear: left;
          border: 0 none;

          .p-column-title {
            padding: .4rem;
            min-width: 30%;
            display: inline-block;
            margin: -.4rem 1rem -.4rem -.4rem;
            font-weight: bold;
          }

          .p-progressbar {
            margin-top: .5rem;
          }
        }
      }
    }
  }

  /deep/ .p-toolbar {
    flex-wrap: wrap;

    .p-button {
      margin-bottom: .25rem;
    }
  }
}
</style>
